// src/components/player/tiles/Tile2ZScoreConventionalPitcher.js
// Tile 2: Conventional Z-Score Analysis (PITCHER VERSION)
// Reads from player_z_scores_mlb cache table canonical structure

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { Award } from 'lucide-react';

const Tile2ZScoreConventionalPitcher = ({ data, loading, error }) => {
  // Tile wrapper component
  const Tile = ({ children }) => (
    <div className={`${dynastyTheme.components.card.base} h-full`}>
      {children}
    </div>
  );

  // Format stat name for display
  const formatStatName = (fieldName) => {
    const labels = {
      'wins': 'W',
      'saves': 'SV',
      'holds': 'HLD',
      'era': 'ERA',
      'whip': 'WHIP',
      'strikeouts_pitched': 'K',
      'quality_starts': 'QS',
      'k_per_9': 'K/9',
      'bb_per_9': 'BB/9',
      'losses': 'L',
      'blown_saves': 'BS'
    };
    return labels[fieldName] || fieldName.toUpperCase();
  };

  // Get Z-score indicator emoji
  const getZScoreIndicator = (zScore) => {
    if (zScore >= 2.0) return '🚀';
    if (zScore >= 1.0) return '⭐';
    if (zScore >= 0.5) return '↑';
    if (zScore >= -0.5) return '→';
    return '↓';
  };

  // Get Z-score color class
  const getZScoreColor = (zScore) => {
    if (zScore >= 2.0) return dynastyTheme.classes.text.success;
    if (zScore >= 1.0) return dynastyTheme.classes.text.primary;
    if (zScore >= -0.5) return dynastyTheme.classes.text.white;
    return dynastyTheme.classes.text.error;
  };

  // Cache table canonical structure:
  // - category_z_scores: {era: 0.8, wins: 1.2, ...}
  // - percentiles: {era: 79, wins: 88, ...}
  // - total_players: 328 (RP) or 641 (SP)
  // - rank: 45
  // - aggregate_z_score: 0.65
  // - position_code: 'SP' or 'RP'
  const hasZScores = data?.category_z_scores && Object.keys(data.category_z_scores).length > 0;
  const hasPercentiles = data?.percentiles;
  const role = data?.position_code === 'SP' ? 'starter' : 'reliever';

  return (
    <Tile>
      <div className="pt-1 px-4 pb-3 flex flex-col tile-scroll" style={{ height: '182px', overflow: 'auto' }}>
        <h4 className={`text-base font-semibold ${dynastyTheme.classes.text.primary} mb-3 flex items-center`}>
          <Award className="w-4 h-4 mr-2" />
          Z-Score Analysis ({role})
        </h4>

        {loading ? (
          <div className={`flex-1 flex items-center justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
              Loading analytics...
            </div>
          </div>
        ) : error ? (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.error} mb-2`}>
              {error}
            </div>
          </div>
        ) : hasZScores ? (
          <div className="flex-1 flex flex-col">
            {/* Z-Score Grid */}
            <div className="flex-1 overflow-y-auto space-y-1">
              {Object.keys(data.category_z_scores).map((stat) => {
                const zScore = data.category_z_scores[stat];
                const percentile = hasPercentiles ? data.percentiles[stat] : null;

                // Skip stats with undefined z_scores
                if (zScore === undefined || zScore === null) {
                  return null;
                }

                return (
                  <div key={stat} className="grid grid-cols-3 gap-2 text-sm items-center py-1">
                    {/* Stat Name */}
                    <div className={`col-span-1 ${dynastyTheme.classes.text.neutralLight}`}>
                      {formatStatName(stat)}
                    </div>

                    {/* Z-Score */}
                    <div className={`col-span-1 text-center`}>
                      <span className={`text-sm ${getZScoreColor(zScore)}`}>
                        {zScore >= 0 ? '+' : ''}{zScore.toFixed(2)} {getZScoreIndicator(zScore)}
                      </span>
                    </div>

                    {/* Percentile */}
                    <div className={`col-span-1 text-right ${dynastyTheme.classes.text.neutral}`}>
                      {percentile ? `${Math.round(percentile)}th` : '-'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer with rank and population info */}
            {data.total_players && (
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} pt-2 mt-2 border-t border-gray-700`}>
                <div>Rank: {data.rank} / {data.total_players} {role}s</div>
                {data.aggregate_z_score && (
                  <div>Aggregate Z: {parseFloat(data.aggregate_z_score).toFixed(2)}</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className={`flex-1 flex flex-col justify-center`}>
            <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} text-center`}>
              No Z-score data available
            </div>
          </div>
        )}
      </div>
    </Tile>
  );
};

export default Tile2ZScoreConventionalPitcher;
